import { Badge, Box, Center, Flex, Text } from '@chakra-ui/react'
import {
  Encounter,
  EncounterStatus,
  encounterTypeName,
  getDateString,
  patientEncounterTypes,
  WithId,
} from '@hb/shared'
import { ReactNode, useMemo, useState } from 'react'
import { LoadingOverlay } from '../../components/LoadingOverlay'
import { PreviewBox } from '../../components/Users/Profile/Body/V2/Containers/PreviewBox'
import { useProfile } from '../../contexts/user/ProfileContext'
import { EncounterViewModal } from './EncounterViewModal'
import { useEncounterStatusLabel } from './useEncounterStatusLabel'

// Status → Badge color, matching the full EncounterPreview card.
const encounterStatusColorScheme: Record<EncounterStatus, string> = {
  [EncounterStatus.DRAFT]: 'gray',
  [EncounterStatus.COMPLETED]: 'blue',
  [EncounterStatus.AUTHENTICATED]: 'green',
}

// Compact, single-line row for the combined chronological encounters list. Provider is shown
// via the stored initials (no per-row user fetch) to keep the preview light.
const EncounterPreviewRow = ({
  encounter,
  onClick,
}: {
  encounter: WithId<Encounter>
  onClick: () => void
}) => {
  const dateLabel = encounter.date ? getDateString(encounter.date, 'short', true) : '—'
  const statusLabel = useEncounterStatusLabel(encounter.status)
  const initials = encounter.renderingProviderInitials
  return (
    <Flex
      onClick={onClick}
      cursor="pointer"
      align="center"
      gap={2}
      w="100%"
      px={3}
      py={2}
      bg="white"
      _hover={{ bg: 'gray.50' }}
      borderBottom="1px solid"
      borderColor="gray.100">
      <Text fontSize="sm" fontWeight={500} w="80px" flexShrink={0}>
        {dateLabel}
      </Text>
      <Text fontSize="sm" flex={1} minW={0} noOfLines={1}>
        {encounterTypeName[encounter.type]}
      </Text>
      {initials ? (
        <Text fontSize="xs" color="gray.500" flexShrink={0}>
          {initials}
        </Text>
      ) : null}
      <Badge
        fontSize="2xs"
        colorScheme={encounterStatusColorScheme[encounter.status]}
        flexShrink={0}>
        {statusLabel}
      </Badge>
    </Flex>
  )
}

// Compact preview of a patient's GYN encounters for the patient control panel. Shows a combined,
// most-recent-first list; a row opens the read-only EncounterView in a modal, and the title links
// out to the full GYN Care → Encounters tab.
export const EncountersPreview = () => {
  const {
    userId,
    goTo: onTabSelect,
    encounters: { dataArr: encountersArr, loading },
  } = useProfile()
  const [openEncounterId, setOpenEncounterId] = useState<string | null>(null)

  // Combined, most-recent-first list of the patient (non-pregnancy) encounter types — the same
  // set shown by the full GYN Care → Encounters tab this preview's title links to.
  const patientEncounters = useMemo(() => {
    if (!encountersArr?.length) return []
    return [...encountersArr]
      .filter(e => patientEncounterTypes.includes(e.type))
      .sort((a, b) => {
        const aDate = a.date ? new Date(a.date).getTime() : 0
        const bDate = b.date ? new Date(b.date).getTime() : 0
        return bDate - aDate
      })
  }, [encountersArr])

  let body: ReactNode = null
  if (loading && !encountersArr?.length) {
    body = <LoadingOverlay text="Loading encounters..." />
  } else if (!patientEncounters.length) {
    body = (
      <Center w="100%" p={4}>
        <Text fontSize="sm" color="gray.500">
          No encounters found
        </Text>
      </Center>
    )
  } else {
    body = (
      <Flex flexFlow="column" w="100%">
        {patientEncounters.map(encounter => (
          <EncounterPreviewRow
            key={encounter.id}
            encounter={encounter}
            onClick={() => setOpenEncounterId(encounter.id)}
          />
        ))}
      </Flex>
    )
  }

  return (
    <PreviewBox
      fixed
      h="325px"
      title="Encounters"
      onGo={() => onTabSelect({ entity: { type: 'patient' }, tab: 'gyn/encounters' })}>
      <Box w="100%" h="100%" overflowY="auto">
        {body}
      </Box>
      {openEncounterId && userId ? (
        <EncounterViewModal
          examId={openEncounterId}
          patientId={userId}
          onClose={() => setOpenEncounterId(null)}
        />
      ) : null}
    </PreviewBox>
  )
}
